import axios from "axios";

interface Selection {
  id: number;
  timeline: string;
  selection: string;
  patientId: number;
  isDefault: boolean;
}

/**
 * Function to update the selection data in the backend
 *
 * @param role - The role of the user ("practitioner" or "patient")
 * @param patientId - The ID of the patient whose selection is being updated
 * @param result - The result to transform into selection data to be updated
 * @returns Updated selection data or throws an error if the request fails
 */
export async function updateSelectionBilan(
  role: "practitioner" | "patient",
  result: ResultItem[],
  patientId?: string,
): Promise<Selection | undefined> {
  try {
    // Prepare the data to send in the request
    const selection = processResultsToSelection({
      resultsBilan: JSON.stringify(result),
    });
    const adaptedData = {
      selection: JSON.stringify(selection),
    };

    let apiUrl: string;
    if (role === "practitioner") {
      if (!patientId) {
        throw new Error("Patient ID is required for practitioners.");
      }
      apiUrl = `/practitioner/visualfield/${patientId}/timeline/Automatique/selection`;
    } else {
      apiUrl = `/patient/visualfield/timeline/Automatique/selection`;
    }

    // Make the PUT request to update the selection
    const response = await axios.put(
      apiUrl,
      adaptedData
    );

    // Return the updated selection data
    return response.data;
  } catch (error: unknown) {  // Use 'unknown' instead of 'any'
    // Log the error for debugging purposes
    console.error("Error while updating the selection:", error);

    if (axios.isAxiosError(error)) {
      // Handle Axios-specific error
      if (error.response) {
        // Server responded with a status code outside of the 2xx range
        console.error("Response error data:", error.response.data);
        throw new Error(
          `Failed to update selection: ${error.response.data?.message || 'Unknown error'}`
        );
      } else if (error.request) {
        // Request was made, but no response was received (network issues, etc.)
        console.error("No response received:", error.request);
        throw new Error("No response received from the server. Please try again.");
      }
    } else if (error instanceof Error) {
      // Handle other types of errors (e.g., client-side issues)
      console.error("Error message:", error.message);
      throw new Error(`Error: ${error.message}`);
    } else {
      // Handle unknown types of errors
      throw new Error("An unknown error occurred.");
    }
  }
}

// Définition des types
type MaillageType = number[][];
type SelectedCellsType = number[][];

// Objet attendu dans selectionRef.current
interface SelectionFormat {
  ZF: string[];
  ZP: string[];
  ZV: string[];
}

// Pour un résultat de type JSON dans resultsBilan
interface ResultItem {
  position: string;
  reactionTime?: string | number;
  successCount?: string | number;
}

interface ResultData {
  resultsBilan: string;
}

// Fonction utilitaire : Conversion d'une position (ex: "A1") en indices (row et col)
const convertPositionToIndices = (position: string) => {
  const letter = position.charAt(0);
  const number = parseInt(position.slice(1), 10);
  const col = letter.charCodeAt(0) - 65; // 'A' → 0, 'B' → 1, etc.
  const row = number - 1; // 1 → 0, 2 → 1, etc.
  return { row, col };
};

// Fonction utilitaire : Conversion d'indices en notation de grille (ex: (0,0) -> "A1")
const convertToGridNotation = (rowIndex: number, colIndex: number): string => {
  const column = String.fromCharCode(65 + colIndex); // 0 → 'A', 1 → 'B', etc.
  const row = rowIndex + 1;
  return `${column}${row}`;
};

// 1. Transformation des résultats bruts en maillage 12x16
const transformResultsToMaillage = (resultData: ResultData): MaillageType => {
  // Initialisation de la grille (12 lignes x 16 colonnes) remplie de 0
  const newMaillage: MaillageType = [...Array(12)].map(() => Array(16).fill(0));
  try {
    const jsonResults: ResultItem[] = JSON.parse(resultData.resultsBilan);
    if (jsonResults && jsonResults.length > 0) {
      // Si le premier résultat possède une propriété reactionTime, on incrémente la valeur si ce n'est pas "Trop long"
      if (jsonResults[0].reactionTime !== undefined) {
        jsonResults.forEach((result) => {
          if (result.reactionTime !== "Trop long") {
            const { row, col } = convertPositionToIndices(result.position);
            newMaillage[row][col] += 1;
          }
        });
      } else {
        // Sinon, on se base sur successCount en assignant directement
        jsonResults.forEach((result) => {
          const { row, col } = convertPositionToIndices(result.position);
          newMaillage[row][col] = Number(result.successCount);
        });
      }
    }
  } catch (error) {
    console.error("Failed to parse resultData:", error);
  }
  return newMaillage;
};

// 2. Auto-sélection des cellules à partir du maillage
// La logique :
// - Si la cellule a la valeur 0, on la marque en rouge (2)
// - Si la cellule a la valeur 1 ou 2, on la marque en jaune (1)
// - Si la cellule a la valeur 3, on la marque en vert (3)
// Puis, en second passage, pour chaque cellule à 3, on regarde ses 8 voisins et si un voisin vaut 0, on lui donne la valeur jaune (1)
const autoSelectCells = (maillage: MaillageType): SelectedCellsType => {
  const rows = maillage.length;
  const cols = maillage[0]?.length || 0;

  // Initialisation de la grille de sélection identique au maillage
  const newSelectedCells: SelectedCellsType = Array.from({ length: rows }, () =>
    Array(cols).fill(0)
  );

  // Premier passage
  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      if (maillage[row][col] === 0) {
        // On marque la cellule rouge (2) pour une valeur 0
        newSelectedCells[row][col] = 2;
      } else if (maillage[row][col] === 1 || maillage[row][col] === 2) {
        // On marque la cellule jaune (1) pour une valeur 1 ou 2
        newSelectedCells[row][col] = 1;
      } else if (maillage[row][col] === 3) {
        // On marque la cellule verte (3) pour une valeur 3
        newSelectedCells[row][col] = 3;
      }
    }
  }

  // Second passage : pour chaque cellule de valeur 3, modifier ses voisins à 0 pour qu'ils deviennent jaunes (1)
  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      if (maillage[row][col] === 3) {
        // Parcourir les 8 voisins
        for (let i = -1; i <= 1; i++) {
          for (let j = -1; j <= 1; j++) {
            if (i === 0 && j === 0) continue; // Ignorer la cellule centrale
            const neighborRow = row + i;
            const neighborCol = col + j;
            // Vérifier que le voisin se trouve dans la grille
            if (
              neighborRow >= 0 &&
              neighborRow < rows &&
              neighborCol >= 0 &&
              neighborCol < cols
            ) {
              if (maillage[neighborRow][neighborCol] === 0) {
                newSelectedCells[neighborRow][neighborCol] = 1;
              }
            }
          }
        }
      }
    }
  }

  return newSelectedCells;
};

// 3. Conversion de la grille de sélection en objet conforme à selectionRef.current
const convertSelectedCellsToSelection = (selectedCells: SelectedCellsType): SelectionFormat => {
  const selectionResult: SelectionFormat = { ZF: [], ZP: [], ZV: [] };

  selectedCells.forEach((row, rowIndex) => {
    row.forEach((cell, colIndex) => {
      // Convention : 
      // 1 → Zone Frontière (ZF = Jaune)
      // 2 → Zone Profonde (ZP = Rouge)
      // 3 → Zone Saine (ZV = Vert)
      if (cell === 1) {
        selectionResult.ZF.push(convertToGridNotation(rowIndex, colIndex));
      } else if (cell === 2) {
        selectionResult.ZP.push(convertToGridNotation(rowIndex, colIndex));
      } else if (cell === 3) {
        selectionResult.ZV.push(convertToGridNotation(rowIndex, colIndex));
      }
    });
  });

  return selectionResult;
};

// 4. Fonction complète de transformation : résultats bruts -> maillage -> auto-sélection -> format selection
const processResultsToSelection = (resultData: ResultData): SelectionFormat => {
  // Transformation des résultats en maillage
  const maillage = transformResultsToMaillage(resultData);
  // Auto-sélection des cellules à partir du maillage
  const autoSelectedCells = autoSelectCells(maillage);
  // Conversion du tableau de sélection en objet au format selectionRef.current
  const selection = convertSelectedCellsToSelection(autoSelectedCells);
  return selection;
};

// // Exemple d'utilisation
// const resultDataExample: ResultData = {
//   resultsBilan:
//     '[{"position": "A1", "successCount": "2"}, {"position": "B2", "reactionTime": 300}, {"position": "C3", "reactionTime": "Trop long"}, {"position": "D4", "successCount": "3"}]',
// };

// const selectionFormatResult = processResultsToSelection(resultDataExample);

// console.log("Format de sélection attendu :", selectionFormatResult);